export interface PhoneConfig {
  clientId?: string;
  clientName?: string;
  provider?: string;
  phoneNumber: string;
  isActive?: boolean;
  createdAt?: string | Date;
  createdBy?: string;
}

export type CreatePhoneConfig = Omit<PhoneConfig, 'createdAt' | 'createdBy'>;
export type UpdatePhoneConfig = Partial<PhoneConfig>;

/**
 * One entry of `clients/{clientId}.phoneConfigs` — the source of truth for a client's
 * outbound lines (NP-04). The legacy single `phoneConfig` object still exists as a mirror
 * of the primary entry for legacy readers; new readers must prefer `phoneConfigs` and only
 * fall back to `phoneConfig` when the list is absent (see `getClientPhoneConfigs`).
 */
export interface PhoneConfigEntry {
  /** Backend-generated, immutable. The PATCH route keys off this, not off `phoneNumber`. */
  id: string;
  phoneNumber: string;
  isActive: boolean;
  provider: string;
  /**
   * 2-letter state/province the number itself belongs to, derived from its area code by the
   * backend. `null` for non-geographic ranges — toll-free, premium-rate, government — and
   * toll-free specifically is what BRD-INT-2026-001 forbids presenting to a patient.
   * Display only — never collected from the user.
   */
  region: string | null;
  /** Country of the area code. `+1` is NANP-wide, so a `+1` number is not necessarily US. */
  isoCountry: NanpCountry | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

/** Country a NANP area code belongs to. Mirrors `NanpCountry` in welby-micro-service. */
export type NanpCountry = 'US' | 'CA' | 'OTHER';


/**
 * Human names for the non-US regions a NANP area code can resolve to.
 *
 * `region` is only a US state code when `isoCountry` is `'US'`. The two-letter codes collide across
 * the NANP, so rendering the bare code is not merely terse, it is misleading: `KY` is Kentucky *and*
 * the Cayman Islands (NPA 345), `MS` is Mississippi *and* Montserrat (NPA 664). Naming the place is
 * what stops an admin from reading a Caribbean line as a domestic one — the same collision the
 * backend's `isoCountry === 'US'` eligibility rule exists to catch.
 *
 * Mirrors the keys of `CA_AREA_CODES_BY_REGION` and `OTHER_AREA_CODES_BY_REGION` in
 * welby-micro-service (`core/client/phone/nanp-area-codes.ts`). A code absent from this map degrades
 * to the bare code rather than guessing a name, so the backend gaining a NANP member — which happens
 * over time — shows something correct-but-terse instead of breaking.
 */
export const NANP_NON_US_REGION_NAMES: Readonly<Record<string, string>> = {
  // Canadian provinces and territories.
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', ON: 'Ontario', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
  // Caribbean and Atlantic NANP members.
  AG: 'Antigua and Barbuda', AI: 'Anguilla', BB: 'Barbados', BM: 'Bermuda', BS: 'Bahamas',
  DM: 'Dominica', DO: 'Dominican Republic', GD: 'Grenada', JM: 'Jamaica',
  KN: 'Saint Kitts and Nevis', KY: 'Cayman Islands', LC: 'Saint Lucia', MS: 'Montserrat',
  SX: 'Sint Maarten', TC: 'Turks and Caicos', TT: 'Trinidad and Tobago',
  VC: 'Saint Vincent and the Grenadines', VG: 'British Virgin Islands',
};

/** POST body. The backend derives `provider` and `region` from `phoneNumber`. */
export type CreatePhoneConfigEntry = Pick<PhoneConfigEntry, 'phoneNumber' | 'isActive'>;

/** PATCH body. Only these two fields are editable once an entry exists. */
export type UpdatePhoneConfigEntry = Partial<Pick<PhoneConfigEntry, 'isActive'>>;
